// The v2 board's hover cards (GRO-276): a short title, then label / value rows with the date
// on the same line. Facts only; no explanatory footers. Wrapped by components/HoverCard.

import type { ReactNode } from 'react';
import { CardRows, CardSection, CardTitle } from '../components/HoverCard';
import {
  bestSignal, monthShort, rankingRows, relationshipPill, signalRowText, ventureSummary, whyNowCell, whyNowPassRows,
} from './boardCells';
import { accountCardFacts, accountCardProfile } from './accountCard';
import { agentUsageRows } from './agentUsage';
import type { AccountCardFact } from './accountCard';
import { lastOutbound } from './derived';
import type { RankedAccount } from './derived';
import { formatUsd, readFunding } from './funding';
import { formatCount, titleCase } from '../lib/format';
import { ScoreCardBody, useScoreDetail } from './ScoreCardBody';
import { readinessReading, scoreOf } from './scores';

const SIGNAL_LABELS: Record<string, string> = {
  usage_climbing: 'Usage', usage_stopped: 'Usage', paid_admin: 'Paid admin', playground_user: 'Playground',
  replied: 'Replied', job_posting: 'Hiring', job_changer: 'New hire', funding: 'Funding',
  web_intent: 'Web visits', shared_investor: 'Investor', paid_tier: 'Plan', clay_signal: 'Clay',
  lookalike: 'Lookalike', product_launch: 'Launch', other_voice_provider: 'Voice provider',
  voice_product: 'Voice product', voice_platform: 'Voice platform',
  q4_research: 'Q4 research', q4_research_unreviewed: 'Q4 research (unreviewed Exa news)',
};

export function WhyNowCard({ row }: { row: RankedAccount }) {
  const reading = whyNowCell(row);
  if (reading?.pass) return <WhyNowPassCard row={row} />;
  const signal = bestSignal(row);
  const rows: Array<[string, string]> = [];

  // The best signal in full, then the next two.
  // Each row's http(s) links render as links (CardRows); a link held only in `url` is appended.
  for (const entry of (row.signals ?? (signal ? [signal] : [])).slice(0, 3)) {
    rows.push([SIGNAL_LABELS[entry.kind] ?? titleCase(entry.kind), signalRowText(entry)]);
  }

  // Scout's ranking and, when it adds a fact, its reason.
  rows.push(...rankingRows(row));
  if (!rows.length) return <CardTitle>No signal yet</CardTitle>;
  return <><CardTitle>{reading?.phrase ?? 'Why now'}</CardTitle><CardRows rows={rows} /></>;
}

// The weekly pass's line: its detail, the board fact it rests on, and when it was written.
function WhyNowPassCard({ row }: { row: RankedAccount }) {
  const rows = whyNowPassRows(row);
  return <><CardTitle>{row.why_now!.line}</CardTitle><CardRows rows={rows} /></>;
}

export function RelationshipCard({ row }: { row: RankedAccount }) {
  const pill = relationshipPill(row);
  const deals: Array<[string, string]> = row.opportunities.map(deal => [
    deal.won ? 'Won' : deal.closed ? 'Lost' : 'Open',
    [deal.stage, deal.qualified_pipeline != null ? formatUsd(deal.qualified_pipeline) : null,
      deal.close_date || deal.created].filter(Boolean).join(' · '),
  ]);
  const facts: Array<[string, string]> = [
    ['Plan', row.tier ? titleCase(row.tier) : 'None'],
    ['Self-serve orgs', formatCount(row.org_count)],
    ['Credits 30d', row.credits_30 == null ? '—' : formatCount(row.credits_30)],
    ['Touches', formatCount(row.touches)],
  ];
  return (
    <>
      <CardTitle>{pill.label}</CardTitle>
      {deals.length ? <CardSection title="Deals"><CardRows rows={deals} /></CardSection> : null}
      <CardSection title="Self-serve"><CardRows rows={facts} /></CardSection>
    </>
  );
}

// Our funds on the cap table: a section of the Funding card, since the board folded Our VC into
// Funding. "None of ours" and "Couldn't find out" are one line each.
function VentureSection({ row }: { row: RankedAccount }) {
  const summary = ventureSummary(row);
  if (summary.kind === 'unknown') return <CardSection title="Our funds"><CardRows rows={[['Couldn’t find out', summary.hint]]} /></CardSection>;
  if (summary.kind === 'none') return <CardSection title="Our funds"><CardRows rows={[['None of ours', 'Apollo investor list']]} /></CardSection>;
  return (
    <CardSection title="Our funds">
      <dl className="hcrows">
        {summary.ties.map(tie => (
          <div key={tie.fund} className="hcrow">
            <dt className={tie.in_latest ? 'accent' : undefined}>{tie.short_name}</dt>
            <dd>{tie.in_latest ? 'Latest round' : 'Older stake'}{tie.rounds ? ` · ${tie.rounds}` : ''}</dd>
          </div>
        ))}
      </dl>
    </CardSection>
  );
}

// The latest round plus every other round our funds' ties mention, newest first.
function roundHistory(row: RankedAccount): Array<[string, string]> {
  const rounds = new Map<string, string>();
  const latest = row.funding?.latest_round;
  if (latest?.date) rounds.set(latest.date, `${latest.label}${latest.amount_usd ? ' · ' + formatUsd(latest.amount_usd) : ''}`);
  for (const tie of row.funding?.relationship.funds ?? []) {
    for (const round of tie.rounds) {
      if (round.date && !rounds.has(round.date)) rounds.set(round.date, round.type || 'Round');
    }
  }
  return [...rounds.entries()]
    .sort((left, right) => right[0].localeCompare(left[0]))
    .map(([month, label]) => [monthShort(month), label]);
}

// The old Our VC column's card, for the board without scores: there the column stays, as on main
// before scoring (GRO-460 shadow mode). With scores on, the funds fold into the Funding card instead.
export function VentureCard({ row }: { row: RankedAccount }) {
  const summary = ventureSummary(row);
  if (summary.kind === 'unknown') return <><CardTitle>Couldn’t find out</CardTitle><CardRows rows={[['Why', summary.hint]]} /></>;
  if (summary.kind === 'none') return <><CardTitle>None of our funds</CardTitle><CardRows rows={[['Source', 'Apollo investor list']]} /></>;
  return (
    <>
      <CardTitle>Our funds</CardTitle>
      <dl className="hcrows">
        {summary.ties.map(tie => (
          <div key={tie.fund} className="hcrow">
            <dt className={tie.in_latest ? 'accent' : undefined}>{tie.short_name}</dt>
            <dd>{tie.in_latest ? 'Latest round' : 'Older stake'}{tie.rounds ? ` · ${tie.rounds}` : ''}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

// `withFunds`: the Our funds section under the rounds, drawn when the Our VC column is folded in
// (scores shown). Without scores the board keeps its own Our VC column, so the card leaves it out.
export function FundingCard({ row, withFunds = true }: { row: RankedAccount; withFunds?: boolean }) {
  const reading = readFunding(row);
  const funding = row.funding;
  if (reading.kind === 'unknown' || !funding) {
    return <><CardTitle>No funding on file</CardTitle><CardRows rows={[['Why', reading.hint]]} />{withFunds ? <VentureSection row={row} /> : null}</>;
  }
  const facts: Array<[string, string]> = [];
  if (funding.total_funding_usd) facts.push(['Total raised', formatUsd(funding.total_funding_usd)]);
  if (funding.public?.symbol) facts.push(['Ticker', funding.public.symbol]);
  if (funding.acquired) facts.push(['Acquired', [funding.acquired.by, monthShort(funding.acquired.date)].filter(Boolean).join(' · ')]);
  facts.push(['Source', funding.matched_by === 'name' ? 'Apollo, matched by name' : 'Apollo']);
  if (funding.fetched_at) facts.push(['Checked', funding.fetched_at.slice(0, 10)]);
  const history = roundHistory(row);
  return (
    <>
      <CardTitle>{reading.label}</CardTitle>
      {history.length ? <CardSection title="Rounds"><CardRows rows={history} /></CardSection> : null}
      <CardRows rows={facts} />
      {withFunds ? <VentureSection row={row} /> : null}
    </>
  );
}

export function LastTouchCard({ row }: { row: RankedAccount }) {
  const last = lastOutbound(row);
  if (!last) return <CardTitle>Never contacted</CardTitle>;
  const entry = row.timeline.find(item => item.direction === 'sent');
  const rows: Array<[string, string]> = [
    ['To', last.who || '—'],
    ['By', (last.author || '—') + (last.byOther ? ' (not the owner)' : '')],
    ['Channel', entry?.channel ? titleCase(entry.channel) : '—'],
    ['Date', last.day],
  ];
  if (entry?.campaign) rows.push(['Campaign', entry.campaign]);
  // Only a received row is a reply; an unresolved one is not.
  const reply = row.timeline.find(item => item.direction === 'received');
  if (reply) rows.push(['Last reply', `${reply.who || 'Someone'} · ${reply.day}`]);
  return <><CardTitle>Last touch</CardTitle><CardRows rows={rows} /></>;
}

export function UsageCard({ row }: { row: RankedAccount }) {
  const rows: Array<[string, string]> = [
    ['Credits 30d', row.credits_30 == null ? '—' : formatCount(row.credits_30)],
    ['Previous 30d', row.credits_prior_30 == null ? '—' : formatCount(row.credits_prior_30)],
    ['Change', row.credits_trend_pct == null ? '—' : `${row.credits_trend_pct > 0 ? '+' : ''}${Math.round(row.credits_trend_pct)}%`],
    // Managed Agents bill in minutes and dollars, so they sit beside the credits, not in them (GRO-450).
    ...agentUsageRows(row),
    ['Plan', row.tier ? titleCase(row.tier) : 'None'],
    ['Self-serve orgs', formatCount(row.org_count)],
  ];
  if (row.orgs.some(org => org.overage)) rows.push(['Overage', 'An org is over its plan']);
  return <><CardTitle>Self-serve usage</CardTitle><CardRows rows={rows} /></>;
}

export function VerticalCard({ row }: { row: RankedAccount }) {
  const engine = row.clay?.engine;
  const rows: Array<[string, string]> = [];
  const seen = new Set<string>();

  // Three sources that often agree: "Healthcare" three times over is one fact, shown once.
  for (const [label, value] of [['Sector', engine?.sector], ['Industry', engine?.industry], ['Salesforce', row.industry]] as const) {
    if (!value || seen.has(value.trim().toLowerCase())) continue;
    seen.add(value.trim().toLowerCase());
    rows.push([label, value]);
  }
  return <><CardTitle>Vertical</CardTitle><CardRows rows={rows} /></>;
}

export function ScoreCard({ row, kind }: { row: RankedAccount; kind: 'fit' | 'engagement' }) {
  const score = scoreOf(row);
  if (!score) return <CardTitle>Not scored yet</CardTitle>;
  return <ScoreCardBody accountId={row.id} score={score} kind={kind} />;
}

export function ReadinessCard({ row }: { row: RankedAccount }) {
  const score = scoreOf(row);
  const reading = readinessReading(row);
  const { detail, detailState } = useScoreDetail(row.id);
  if (!score || !reading) return <CardTitle>Not scored yet</CardTitle>;
  // One reason per line, with no label: a "Why" in front of each only repeated itself.
  const reasons = detail?.readiness_reasons ?? [];
  const placeholder = detailState === 'loading' ? 'Loading…'
    : detailState === 'failed' ? 'Reasons could not be loaded' : 'No size or funding facts on file';
  return (
    <>
      <CardTitle>{reading.label}</CardTitle>
      {(reasons.length ? reasons : [placeholder]).map((reason, index) => <p key={index} className="hcnote">{reason}</p>)}
    </>
  );
}

// The Account cell's card: the full name, size and industry, then every flag spelled out.
// A pick whose account is not in the book yet has only a name and its request flags.
export function AccountCard({
  row, name, today, requestFacts,
}: { row: RankedAccount | null; name: string; today: Date; requestFacts?: AccountCardFact[] }) {
  const profile = row ? accountCardProfile(row) : null;
  const profileRows: Array<[string, string]> = [];
  if (profile?.employees) profileRows.push(['Size', profile.employees]);
  if (profile?.industry) profileRows.push(['Industry', profile.industry]);

  // The Slack row links to the channel when Salesforce holds one.
  const facts = row ? accountCardFacts(row, today, requestFacts) : (requestFacts ?? []);
  const flagRows: Array<[string, ReactNode]> = facts.map(fact => [
    fact.label,
    fact.url
      ? <a href={fact.url} target="_blank" rel="noopener">{fact.detail} (open channel)</a>
      : fact.detail,
  ]);
  return (
    <>
      <CardTitle>{name}</CardTitle>
      {profileRows.length ? <CardRows rows={profileRows} /> : null}
      <CardSection title="Flags">
        {flagRows.length ? <CardRows rows={flagRows} /> : <div className="hcnone">None</div>}
      </CardSection>
    </>
  );
}
